import { useMutation, useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import Button from 'shopper/components/Button';
import Icon from 'shopper/components/Icon';

import useQueryClient from 'hooks/useQueryClient';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';
import { threadQueryKeys } from 'lib/queryKeys';

import { useUser } from 'providers/UserProvider';

import { getThreadRating, postThreadLike } from 'services/thread';

import FORUM from 'constants/forum';

const ThreadFeedback = ({
  threadTypeName,
  threadId,
  onAddLike,
  gaEventCategory,
}) => {
  const { isLoggedIn, runCallbackIfLoggedIn } = useUser();
  const queryClient = useQueryClient();

  const { data: threadRating } = useQuery({
    queryKey: threadQueryKeys.isLiked({ threadId }),
    queryFn: ({ signal }) => getThreadRating({ threadId }, { signal }),
    enabled: isLoggedIn,
  });

  const { mutate: mutateLike } = useMutation({
    mutationFn: postThreadLike,
    onSuccess: ({ action }) => {
      queryClient.setQueryData(threadQueryKeys.isLiked({ threadId }), {
        action,
      });

      if (FORUM.THREAD.ACTIONS.LIKE === action) {
        sendEvent({
          action:
            threadTypeName === FORUM.THREAD.TYPES.QUESTION
              ? 'topic_was_helpful'
              : 'same_doubt',
          category: gaEventCategory,
        });
      }

      onAddLike(action);
    },
  });

  const onLikeButtonClick = () => {
    if (!isLoggedIn) {
      sendEvent({
        action: 'like_attempt_unlogged',
        category: gaEventCategory,
      });
    }

    runCallbackIfLoggedIn(mutateLike, { threadId });
  };

  return (
    <Button
      className="mr-2"
      iconLeft={
        <Icon
          name={
            threadRating?.action === FORUM.THREAD.ACTIONS.LIKE
              ? 'like'
              : 'like-filled'
          }
        />
      }
      size="size3"
      type={
        threadRating?.action === FORUM.THREAD.ACTIONS.LIKE
          ? 'secondary'
          : 'secondary-stroke'
      }
      onClick={onLikeButtonClick}
    >
      {placeholder('actions.like')}
    </Button>
  );
};

ThreadFeedback.propTypes = {
  threadId: PropTypes.number.isRequired,
  gaEventCategory: PropTypes.string.isRequired,
  onAddLike: PropTypes.func.isRequired,
};

export default ThreadFeedback;
